import { Caption, Icon, Text, cn } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { type Org } from './care-library-data';
import { LINE_SWATCHES } from './offering-types';

/** A selected (facet, category) pair, or `null` for "All offerings". */
export interface CategorySelection {
  facetId: string;
  catId: string;
}

const railRow =
  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs text-text-secondary transition-colors hover:bg-bg-secondary';
const railRowOn =
  'bg-bg-secondary font-semibold text-text-primary ring-1 ring-inset ring-border-primary';

/**
 * Option A — the left category tree. Lists every org-defined grouping axis
 * (service line / body region / delivery) with per-category counts, plus an
 * "All offerings" entry. Selecting a row filters the grid; the primary axis is
 * flagged and its rows carry a swatch.
 */
export const CategoryRail: React.FC<{
  org: Org;
  selected: CategorySelection | null;
  onSelect: (selection: CategorySelection | null) => void;
}> = ({ org, selected, onSelect }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1 border-r border-border-primary bg-bg-secondary/40 p-3.5">
      <div className="flex items-center gap-2 px-1 pb-2">
        <Icon name="Layers" size="sm" className="text-text-tertiary" />
        <Text inline size="xs" weight="semibold">
          {t('careLibrary.rail.browseByCategory')}
        </Text>
      </div>

      <button
        type="button"
        onClick={() => onSelect(null)}
        className={cn(railRow, selected === null && railRowOn)}
      >
        <span className="flex-1">{t('careLibrary.browse.allOfferings')}</span>
        <span className="font-mono text-2xs text-text-supporting">
          {org.items.length}
        </span>
      </button>

      {org.facets.map((facet) => (
        <div key={facet.id} className="flex flex-col gap-px">
          <div className="mt-3 mb-1 flex items-center gap-1.5 px-1">
            <Caption size="3xs" color="supporting">
              {facet.name}
            </Caption>
            {facet.primary && (
              <Text inline size="xs" color="supporting" className="lowercase">
                {t('careLibrary.rail.primaryFacet')}
              </Text>
            )}
          </div>
          {facet.cats.map((cat, i) => {
            const on =
              selected?.facetId === facet.id && selected?.catId === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() =>
                  onSelect(on ? null : { facetId: facet.id, catId: cat.id })
                }
                className={cn(railRow, on && railRowOn)}
              >
                {facet.primary && (
                  <span
                    className={cn(
                      'size-2 shrink-0 rounded-xs',
                      LINE_SWATCHES[i % LINE_SWATCHES.length],
                    )}
                  />
                )}
                <span className="min-w-0 flex-1 truncate">{cat.name}</span>
                <span className="font-mono text-2xs text-text-supporting">
                  {cat.count}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};
